import { useCallback, useEffect, useRef, useState } from "react";
import { api, WS_BASE } from "@/lib/api";

// state: connecting | waiting | live | offline | closed | denied
export function useRemoteSession(machineId) {
  const canvasRef = useRef(null);
  const wsRef = useRef(null);
  const listeners = useRef(new Set());
  const [state, setState] = useState("connecting");
  const [messages, setMessages] = useState([]);
  const [fps, setFps] = useState(0);
  const [res, setRes] = useState("");
  const [monitors, setMonitors] = useState({ list: [], current: 1 });
  const [streamError, setStreamError] = useState("");

  useEffect(() => {
    let cancelled = false;
    let retry = null;
    let tries = 0;
    let frames = 0;
    let chain = Promise.resolve();
    const timer = setInterval(() => { setFps(frames); frames = 0; }, 1000);
    const ack = () => { const ws = wsRef.current; if (ws?.readyState === 1) ws.send('{"type":"frame_ack"}'); };

    // Frames are drawn in order (partial updates depend on the previous picture), then acknowledged
    // so the agent never sends faster than this connection can display.
    const paint = async (blob) => {
      try {
        const head = new Uint8Array(await blob.slice(0, 16).arrayBuffer());
        const delta = head[0] === 84 && head[1] === 67 && head[2] === 68 && head[3] === 49; // "TCD1"
        const v = new DataView(head.buffer);
        const [x, y, fw, fh] = delta ? [v.getUint16(4), v.getUint16(6), v.getUint16(12), v.getUint16(14)] : [0, 0, 0, 0];
        const bmp = await createImageBitmap(delta ? blob.slice(16) : blob);
        const c = canvasRef.current;
        if (c) {
          const W = delta ? fw : bmp.width, H = delta ? fh : bmp.height;
          if (c.width !== W || c.height !== H) {
            c.width = W;
            c.height = H;
            setRes(`${W}×${H}`);
          }
          c.getContext("2d").drawImage(bmp, x, y);
        }
        bmp.close();
        frames += 1;
        setState("live");
        setStreamError("");
      } catch {
        // ignore corrupted frame
      } finally {
        ack();
      }
    };
    const draw = (blob) => { chain = chain.then(() => paint(blob)); };

    const open = () => {
      const ws = new WebSocket(`${WS_BASE}/api/ws/viewer/${machineId}`);
      ws.binaryType = "blob";
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        if (typeof ev.data !== "string") return draw(ev.data);
        const m = JSON.parse(ev.data);
        if (m.type?.startsWith("fs_") || m.type?.startsWith("cmd_") || m.type === "clipboard" || m.type === "audio") return listeners.current.forEach((fn) => fn(m));
        if (m.type === "monitors") setMonitors(m);
        if (m.type === "stream_status") setStreamError(m.error || "");
        if (m.type === "status") setState(m.online ? "waiting" : "offline");
        if (m.type === "agent_offline") setState("offline");
        if (m.type === "agent_online") setState("waiting");
        if (m.type === "chat") setMessages((p) => [...p, m]);
      };
      ws.onclose = (e) => {
        const final = { 4401: "denied", 4402: "unpaid", 4403: "busy" }[e.code];
        if (final || cancelled || e.code === 1000) return setState(final || "closed");
        // Network blip or server restart: reconnect on our own instead of ending the session.
        setState("reconnecting");
        retry = setTimeout(open, Math.min(1000 * 2 ** tries, 8000));
        tries += 1;
      };
      ws.onopen = () => { tries = 0; };
    };

    // Fetch first so an expired access cookie gets refreshed before the websocket handshake
    api
      .get(`/machines/${machineId}/chat`)
      .then(({ data }) => setMessages(data))
      .catch(() => {})
      .finally(() => !cancelled && open());

    return () => {
      cancelled = true;
      clearInterval(timer);
      clearTimeout(retry);
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
    };
  }, [machineId]);

  const send = useCallback((obj) => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(obj));
  }, []);

  const subscribe = useCallback((fn) => {
    listeners.current.add(fn);
    return () => listeners.current.delete(fn);
  }, []);

  return { canvasRef, state, messages, fps, res, monitors, send, subscribe, streamError };
}
